import type { ComponentProps, MouseEvent } from 'react';
import styled from '@emotion/styled';
import type {
  BorderColorProps,
  BorderProps,
  BorderRadiusProps,
  BorderStyleProps,
  BorderWidthProps,
} from 'styled-system';
import {
  backgroundColor,
  border,
  borderColor,
  borderRadius,
  borderStyle,
  borderWidth,
  themeGet,
} from 'styled-system';

import type { BackgroundColorProps } from '../../../theme/system-types';
import type {
  CursorProps,
  ThemeHeightProps,
  ThemeWidthProps,
} from '../../../theme/system-utilities';
import { cursor, themeHeight, themeWidth } from '../../../theme/system-utilities';
import { Flex } from '../../Flex';

export const ControlButton = styled(Flex)<
  ThemeWidthProps &
    ThemeHeightProps &
    BorderProps &
    BorderStyleProps &
    BorderWidthProps &
    BorderColorProps &
    BorderRadiusProps &
    BackgroundColorProps &
    CursorProps & {
      arrowSize?: string | number;
      onClick?: (e: MouseEvent<HTMLDivElement>) => void;
      name?: string;
      disabled?: boolean;
    }
>`
  ${themeHeight};
  ${themeWidth};
  ${borderStyle};
  ${borderWidth};
  ${borderColor};
  ${border};
  ${borderRadius};
  ${backgroundColor};
  ${cursor};

  &[disabled] {
    opacity: 0.5;
  }

  &:hover:not([aria-disabled='true']) {
    border: ${themeGet('borderWidths.medium')} solid ${themeGet('colors.sys.neutral.border.hover')};

    svg {
      stroke: ${themeGet('colors.sys.neutral.icon.hover')};
    }
  }

  &[aria-disabled='true'] {
    background-color: white;
    border: ${themeGet('borderWidths.small')} solid
      ${themeGet('colors.sys.neutral.border.inactive')};
    display: flex;
    opacity: 1;
  }
`;

export const controlButtonStyles: ComponentProps<typeof ControlButton> = {
  as: 'button',
  justifyContent: 'center',
  alignItems: 'center',
  arrowSize: '22px',
  p: 0,
  cursor: 'pointer',
  themeWidth: ['sp_32', null, null, '44px'],
  themeHeight: ['sp_32', null, null, '44px'],
  ml: ['sp_16', null, null, 'sp_24'],
  backgroundColor: 'sys.neutral.background.weak',
  borderColor: 'sys.neutral.border.default',
  borderStyle: 'solid',
  borderWidth: 'small',
  borderRadius: 'circle',
  tabIndex: -1,
};

export interface ControlButtonProps {
  disabled: boolean;
  handleClick: (e: MouseEvent<HTMLDivElement>) => void;
}
